import { useState } from 'react'
import type { AddressCoordinates, UserAddress, UserAddressNew } from '../entities'
import { steps } from '../steps'
import type { Step, StepActionType } from '../types'
import { useDeviceCurrentLocation } from '@/location/device-current-location/use-device-current-location'
import type {
  AddressDetailsSourceValues,
  AddressSearchSourceValues,
} from '@/tools/analytics/events/domains/address-book'
import { useEntrySource } from '@/user/address-book/hooks/use-entry-source'

const initialAddressData: UserAddressNew = {
  addressLine: '',
  tag: null,
  latitude: 0,
  longitude: 0,
  cityCode: '',
  countryCode: '',
  cityName: '',
  kind: 'APARTMENT',
  details: '',
  fields: [],
}

const initialCoordinates: AddressCoordinates = {
  latitude: 0,
  longitude: 0,
}

/**
 * This hook is used only inside AddressBookContext.
 * Do not consume it directly in components because it will create as separate instance
 * with separate lifecycle and useEffect calls. In ui components please use useAddressBookContext
 */
export function useAddressBook() {
  // States
  const [addressData, setAddress] = useState<UserAddress | UserAddressNew>(initialAddressData)
  const [currentStep, setCurrentStep] = useState<Step>('WHERE_TO_DELIVER')
  const [hasRefinedEntrance, setHasRefinedEntrance] = useState(false)
  const [isInArea, setIsInArea] = useState(true)
  const [addressFormTrigger, setAddressFormTrigger] = useState<AddressDetailsSourceValues>()
  const [isMobile, setIsMobile] = useState(true)
  const [entrySource, setEntrySource] = useState<AddressSearchSourceValues>('Other')
  const [originalCoordinates, setOriginalCoordinates] =
    useState<AddressCoordinates>(initialCoordinates)
  const [closeAddressBook, setCloseAddressBook] = useState<() => void>(() => () => {
    throw new Error('AddressBook close function is not defined')
  })
  const [selectAddress, setAddressChangeSuccess] = useState<
    (address: UserAddress | UserAddressNew) => void
  >(() => () => undefined)

  const deviceCurrentLocation = useDeviceCurrentLocation()
  // Methods
  const navigate = (actionType: StepActionType) => {
    const nextStep = steps[currentStep]?.[actionType]
    if (nextStep) {
      setCurrentStep(nextStep)
    }
  }
  const setLocationAddress = (locationAddress: UserAddress | UserAddressNew) => {
    setIsInArea(true)
    setAddress(locationAddress)
  }

  const resetAddress = () => {
    setAddress(initialAddressData)
    setHasRefinedEntrance(false)
    setIsInArea(false)
    setOriginalCoordinates(initialCoordinates)
  }

  //Hooks
  useEntrySource({
    deviceLocation: deviceCurrentLocation.deviceLocation,
    entrySource,
    setEntrySource,
  })
  return {
    addressData,
    currentStep,
    hasRefinedEntrance,
    isInArea,
    addressFormTrigger,
    isMobile,
    originalCoordinates,
    deviceCurrentLocation,
    navigate,
    setAddress,
    setIsInArea,
    resetAddress,
    closeAddressBook,
    setAddressChangeSuccess,
    selectAddress,
    setCloseAddressBook,
    setIsMobile,
    setHasRefinedEntrance,
    setAddressFormTrigger,
    setOriginalCoordinates,
    setLocationAddress,
    entrySource,
  }
}

export type UseAddressBook = ReturnType<typeof useAddressBook>
